/**
 * Module Meals (mode classique) — Phase 1, incrément 10.
 *
 * Port de `vMeals` du proto (l. 1825+) :
 *   - Grille hebdo : 7 jours × midi/soir, étiquette de catégorie,
 *     today surligné. Click sur un slot → modal de choix de recette.
 *   - Carte Grocery : auto / manuel, toggle « Voir/Masquer », ajout
 *     d'item manuel, suppression item manuel, suggestions
 *     petit-déjeuner / goûter (1 click).
 *   - Bouton « + Planifier les repas » (modal de choix de recette
 *     pour le slot actif) et « ✨ Générer le menu de la semaine »
 *     (assigne 7 dîners aléatoires depuis RECIPE_DB).
 *
 * Toutes les chaînes UI passent par `t(key)`.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { getLang, t, tn } from '../../i18n/index.js';
import {
  addGroceryItem,
  clearMeal,
  getCurrentHome,
  regenerateGroceryFromMeals,
  removeGroceryItem,
  setMeal,
  toggleGroceryItem,
} from '../../stores/homes.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import type { ClassicHomeData, GroceryItem, Meal, MealRecipeInfo } from '@nestor/shared-types';
import { modalTemplate } from '../../ui/Modal.js';
import { share } from '../../lib/share.js';
import { showToast } from '../../ui/Toast.js';
import { RECIPE_DB, type Recipe } from './recipes.js';
import {
  getRecipesDbNow,
  iconForCategory,
  loadRecipesDb,
  searchRecipes,
  toRecipe,
  type FullRecipe,
} from './recipesDb.js';
import {
  buildGroceryFromMeals,
  categoryForIngredient,
  groupGroceryByRayon,
  weekDates,
} from './logic.js';
import { bumpUi } from '../../stores/ui.js';
import { editorialHero } from '../../ui/Hero.js';
import { statLine } from '../../ui/StatLine.js';

const MODAL_RECIPE = 'meals:recipe';
const MODAL_MEAL_SHEET = 'meals:sheet';

// Fiche « à suivre » ouverte depuis un repas planifié (alpha).
let sheetSlot: { dayIndex: number; service: 'lunch' | 'dinner' } | null = null;

// État local : slot actuellement édité quand on ouvre le modal recette.
let editingSlot: { dayIndex: number; service: 'lunch' | 'dinner' } | null = null;
// Recherche dans la base complète (alpha, item 9) + fiche affichée.
let recipeQuery = '';
let viewingRecipe: FullRecipe | null = null;
// État local : section grocery déployée ou non. Démarre fermée.
let groceryOpen = false;

// Suggestions FR (port direct proto l. 1868, 1872) — items courts et
// non traduits (constantes de donnée, pas des libellés UI).
const BREAKFAST_SUGGESTIONS = [
  'lait' /* i18n-allow */,
  'céréales' /* i18n-allow */,
  'pain' /* i18n-allow */,
  'beurre' /* i18n-allow */,
];
const SNACK_SUGGESTIONS = [
  'compotes' /* i18n-allow */,
  'biscuits' /* i18n-allow */,
  'fruits' /* i18n-allow */,
  'jus' /* i18n-allow */,
];

// ── Helpers ───────────────────────────────────────────────────────────

function regenerateGrocery(homeId: string): void {
  // Réutilise un compteur monotone pour générer des ids uniques de
  // grocery — l'API du store attend un builder (meals → items).
  let i = 0;
  regenerateGroceryFromMeals(homeId, (meals) =>
    buildGroceryFromMeals(meals, () => `grauto_${Date.now().toString(36)}_${(i++).toString(36)}`),
  );
}

function pickRandomRecipes(count: number): Recipe[] {
  const pool = [...RECIPE_DB];
  const out: Recipe[] = [];
  for (let i = 0; i < count && pool.length > 0; i += 1) {
    const idx = Math.floor(Math.random() * pool.length);
    out.push(pool.splice(idx, 1)[0]!);
  }
  return out;
}

function applyRecipeToSlot(
  homeId: string,
  dayIndex: number,
  service: 'lunch' | 'dinner',
  recipe: Recipe,
  nutrition?: MealRecipeInfo['nutrition'],
): void {
  // Fiche « à suivre » (alpha) : snapshot autonome pour cuisiner.
  const info: MealRecipeInfo = {
    time: recipe.time,
    servings: recipe.bs,
    steps: recipe.steps,
    ing: recipe.ing.map((i) => i.n),
    ...(nutrition ? { nutrition } : {}),
  };
  // Les ingrédients du jour combinent les DEUX services (avant : la
  // dernière recette écrasait l'autre dans les courses).
  const home = getCurrentHome();
  const meals = home && home.mode === 'classic' ? (home.data as ClassicHomeData).meals : [];
  const other = service === 'lunch' ? meals[dayIndex]?.dinnerInfo : meals[dayIndex]?.lunchInfo;
  const combinedIng = [...recipe.ing, ...(other?.ing.map((n) => ({ n })) ?? [])];
  setMeal(homeId, dayIndex, {
    [service]: recipe.name,
    tag: recipe.tag,
    bs: recipe.bs,
    ing: combinedIng,
    [`${service}Info`]: info,
  } as Parameters<typeof setMeal>[2]);
  regenerateGrocery(homeId);
}

/** Catégories sucrées — jamais proposées en PLAT par le générateur
 * (feedback alpha, item 7). Elles restent dans le picker manuel. */
const SWEET_CATEGORIES = new Set(['Desserts', 'Fruits', 'Boulangerie']);

async function generateWeeklyMenu(homeId: string): Promise<void> {
  // Alpha : remplit MIDI ET SOIR (avant : soir uniquement), en
  // puisant dans la base complète quand elle est disponible (variété),
  // sinon dans les 20 recettes du proto.
  const db = await loadRecipesDb();
  const savoury = db.filter((r) => !SWEET_CATEGORIES.has(r.category));
  const pool: Recipe[] =
    savoury.length >= 14
      ? [...savoury]
          .sort(() => Math.random() - 0.5)
          .slice(0, 14)
          .map((r) => toRecipe(r))
      : pickRandomRecipes(14);
  for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
    const lunch = pool[(dayIndex * 2) % pool.length]!;
    const dinner = pool[(dayIndex * 2 + 1) % pool.length]!;
    const lunchInfo: MealRecipeInfo = {
      time: lunch.time,
      servings: lunch.bs,
      steps: lunch.steps,
      ing: lunch.ing.map((i) => i.n),
    };
    const dinnerInfo: MealRecipeInfo = {
      time: dinner.time,
      servings: dinner.bs,
      steps: dinner.steps,
      ing: dinner.ing.map((i) => i.n),
    };
    setMeal(homeId, dayIndex, {
      lunch: lunch.name,
      dinner: dinner.name,
      tag: dinner.tag,
      bs: dinner.bs,
      ing: [...lunch.ing, ...dinner.ing],
      lunchInfo,
      dinnerInfo,
    });
  }
  regenerateGrocery(homeId);
  // Toast (non bloquant) — window.alert gelait la page et cassait
  // l'automatisation ; le toast est cohérent avec le reste de l'app.
  showToast({ message: t('weekly_menu_generated'), kind: 'success' });
}

// ── Sub-templates ─────────────────────────────────────────────────────

/**
 * Nom d'un repas planifié. Si une fiche recette est attachée (alpha),
 * le nom est cliquable et ouvre la fiche « à suivre » (étapes,
 * ingrédients, nutrition) — utile en cuisinant.
 */
function plannedMealName(
  label: string,
  info: MealRecipeInfo | undefined,
  dayIndex: number,
  service: 'lunch' | 'dinner',
): TemplateResult {
  if (!info) return html`<span class=${styles.mealServiceName}>${label}</span>`;
  return html`<button
    type="button"
    class=${styles.mealServiceNameBtn}
    title=${t('recipe_view_sheet')}
    data-testid=${`meal-sheet-${service}-${dayIndex.toString()}`}
    @click=${(): void => {
      sheetSlot = { dayIndex, service };
      openModal(MODAL_MEAL_SHEET);
    }}
  >
    ${label} <span class=${styles.mealSheetHint} aria-hidden="true">${Icon(Icons.book, 12)}</span>
  </button>`;
}

/** Fiche « à suivre » d'un repas planifié (snapshot MealRecipeInfo). */
function mealSheetTemplate(meal: Meal, service: 'lunch' | 'dinner'): TemplateResult {
  const info = service === 'lunch' ? meal.lunchInfo : meal.dinnerInfo;
  const name = service === 'lunch' ? (meal.lunch ?? '') : meal.dinner || meal.m;
  if (!info) return html``;
  const nut = info.nutrition;
  return html`<div class=${styles.recipeSheet} data-testid="meal-sheet">
    <p class=${styles.recipeMeta}>
      ${info.time.toString()} min · ${info.servings.toString()} ${t('recipe_servings')}
    </p>
    ${nut
      ? html`<div class=${styles.nutritionRow}>
          <span>${nut.calories.toFixed(0)} ${t('nut_calories')}</span>
          <span>${nut.proteins.toFixed(0)} g ${t('nut_proteins')}</span>
          <span>${nut.carbs.toFixed(0)} g ${t('nut_carbs')}</span>
          <span>${nut.fat.toFixed(0)} g ${t('nut_fat')}</span>
          <span>${nut.fiber.toFixed(0)} g ${t('nut_fiber')}</span>
        </div>`
      : null}
    <h4 class=${styles.recipeSheetSub}>${t('ingredients_label')}</h4>
    <div class=${styles.recipeChips}>
      ${info.ing.map((i) => html`<span class=${styles.recipeChip}>${i}</span>`)}
    </div>
    <h4 class=${styles.recipeSheetSub}>${t('recipe_steps')}</h4>
    <ol class=${styles.recipeSteps}>
      ${info.steps.map((s) => html`<li>${s}</li>`)}
    </ol>
    ${void name}
  </div>`;
}

function mealRowTemplate(homeId: string, meal: Meal, dayIndex: number): TemplateResult {
  const lunchEmpty = !meal.lunch || !meal.lunch.trim();
  const dinnerEmpty = !meal.dinner || !meal.dinner.trim();
  // Back-compat : si `m` est défini mais `dinner` ne l'est pas, on
  // affiche `m` à la place du soir.
  const dinnerLabel = meal.dinner || meal.m || '';
  return html`
    <div class=${styles.mealRow} data-testid=${`meal-row-${dayIndex.toString()}`}>
      <span class=${classMap({ [styles.mealDay!]: true, [styles.mealDayToday!]: meal.today })}
        >${meal.d}</span
      >
      <div class=${styles.mealServices}>
        <div class=${styles.mealService}>
          <span class=${styles.mealServiceLabel}>${t('service_lunch')}</span>
          ${lunchEmpty
            ? html`<button
                type="button"
                class=${styles.mealServiceBtn}
                data-testid=${`pick-lunch-${dayIndex.toString()}`}
                @click=${(): void => {
                  editingSlot = { dayIndex, service: 'lunch' };
                  openModal(MODAL_RECIPE);
                }}
              >
                + ${t('pick_recipe')}
              </button>`
            : html`${plannedMealName(meal.lunch ?? '', meal.lunchInfo, dayIndex, 'lunch')}
                <button
                  type="button"
                  class=${styles.mealServiceBtn}
                  aria-label=${t('clear_meal_slot')}
                  data-testid=${`clear-lunch-${dayIndex.toString()}`}
                  @click=${(): void => {
                    clearMeal(homeId, dayIndex, 'lunch');
                    regenerateGrocery(homeId);
                  }}
                >
                  ✕
                </button>`}
        </div>
        <div class=${styles.mealService}>
          <span class=${styles.mealServiceLabel}>${t('service_dinner')}</span>
          ${dinnerEmpty && !meal.m
            ? html`<button
                type="button"
                class=${styles.mealServiceBtn}
                data-testid=${`pick-dinner-${dayIndex.toString()}`}
                @click=${(): void => {
                  editingSlot = { dayIndex, service: 'dinner' };
                  openModal(MODAL_RECIPE);
                }}
              >
                + ${t('pick_recipe')}
              </button>`
            : html`${plannedMealName(dinnerLabel, meal.dinnerInfo, dayIndex, 'dinner')}
                <button
                  type="button"
                  class=${styles.mealServiceBtn}
                  aria-label=${t('clear_meal_slot')}
                  data-testid=${`clear-dinner-${dayIndex.toString()}`}
                  @click=${(): void => {
                    clearMeal(homeId, dayIndex, 'dinner');
                    regenerateGrocery(homeId);
                  }}
                >
                  ✕
                </button>`}
        </div>
      </div>
      ${meal.tag ? html`<span class=${styles.mealTag}>${meal.tag}</span>` : null}
    </div>
  `;
}

function weekCard(homeId: string, meals: readonly Meal[]): TemplateResult {
  return html`
    <div class=${styles.card} data-testid="meals-week">
      <div class=${styles.cardTitle}>${t('week')}</div>
      ${meals.map((m, i) => mealRowTemplate(homeId, m, i))}
    </div>
  `;
}

function groceryItemRow(homeId: string, item: GroceryItem): TemplateResult {
  const cat = categoryForIngredient(item.text);
  return html`
    <div class=${styles.groceryItem} data-testid="grocery-item" data-grocery-id=${item.id}>
      <button
        type="button"
        class=${classMap({ [styles.groceryCheck!]: true, [styles.done!]: item.done })}
        aria-label=${item.text}
        data-testid=${`grocery-toggle-${item.id}`}
        @click=${(): void => {
          toggleGroceryItem(homeId, item.id);
        }}
      >
        ${item.done ? '✓' : ''}
      </button>
      <span class=${classMap({ [styles.groceryText!]: true, [styles.done!]: item.done })}
        >${item.text}</span
      >
      <span class=${styles.groceryCat} style=${`background:${cat.color}`}>${cat.cat}</span>
      ${item.src === 'manual'
        ? html`<button
            type="button"
            class=${styles.groceryRemove}
            aria-label=${t('remove')}
            data-testid=${`grocery-remove-${item.id}`}
            @click=${(): void => {
              removeGroceryItem(homeId, item.id);
            }}
          >
            ×
          </button>`
        : null}
    </div>
  `;
}

/** Partage la liste de courses en texte, groupée par rayons (F6). */
async function shareGroceryList(grocery: readonly GroceryItem[]): Promise<void> {
  const lines: string[] = [t('grocery')];
  for (const group of groupGroceryByRayon(grocery)) {
    lines.push('', `— ${group.cat} —`);
    for (const item of group.items) lines.push(`• ${item.text}`);
  }
  const result = await share({ title: t('grocery'), text: lines.join('\n') });
  if (result.via === 'copied') showToast({ message: t('share_copied'), kind: 'success' });
  if (result.via === 'unsupported') showToast({ message: t('share_unsupported'), kind: 'info' });
}

function groceryCard(homeId: string, grocery: readonly GroceryItem[]): TemplateResult {
  const auto = grocery.filter((g) => g.src === 'recipe');
  const manual = grocery.filter((g) => g.src === 'manual');
  const checked = grocery.filter((g) => g.done).length;
  return html`
    <div class=${styles.groceryCard} data-testid="grocery-card">
      <div class=${styles.groceryHeader}>
        <span class=${styles.groceryIcon} aria-hidden="true">${Icon(Icons.cart, 20)}</span>
        <div class=${styles.groceryHeaderText}>
          <div class=${styles.groceryTitle}>${t('grocery')}</div>
          <div class=${styles.groceryStats}>
            ${checked.toString()}/${grocery.length.toString()} ${t('grocery_checked_count')} ·
            ${auto.length.toString()} ${t('grocery_auto_short')} · ${manual.length.toString()}
            ${tn('grocery_manual', manual.length)}
          </div>
        </div>
        ${grocery.length > 0
          ? html`<button
              type="button"
              class=${styles.groceryToggle}
              aria-label=${t('grocery_share')}
              title=${t('grocery_share')}
              data-testid="grocery-share"
              @click=${(): void => {
                void shareGroceryList(grocery);
              }}
            >
              ${Icon(Icons.share, 15)}
            </button>`
          : null}
        <button
          type="button"
          class=${styles.groceryToggle}
          data-testid="grocery-toggle-visibility"
          @click=${(): void => {
            groceryOpen = !groceryOpen;
            bumpUi();
          }}
        >
          ${groceryOpen ? t('hide') : t('view')}
        </button>
      </div>
      ${groceryOpen
        ? html`<div class=${styles.groceryBody} data-testid="grocery-body">
            ${auto.length > 0
              ? html`<div
                    class=${classMap({
                      [styles.groceryGroup!]: true,
                      [styles.groceryGroupAuto!]: true,
                    })}
                  >
                    ${t('from_recipes')}
                  </div>
                  ${groupGroceryByRayon(auto).map(
                    (group) => html`
                      <div class=${styles.rayonHeader} data-testid=${`rayon-${group.cat}`}>
                        <span
                          class=${styles.rayonDot}
                          style=${`background:${group.color}`}
                          aria-hidden="true"
                        ></span>
                        ${group.cat}
                      </div>
                      ${group.items.map((g) => groceryItemRow(homeId, g))}
                    `,
                  )}`
              : null}
            ${manual.length > 0
              ? html`<div
                    class=${classMap({
                      [styles.groceryGroup!]: true,
                      [styles.groceryGroupManual!]: true,
                    })}
                  >
                    ${t('manual')}
                  </div>
                  ${manual.map((g) => groceryItemRow(homeId, g))}`
              : null}
            ${grocery.length === 0
              ? html`<div class=${styles.groceryEmpty}>${t('grocery_will_generate')}</div>`
              : null}
            <form
              class=${styles.groceryAddRow}
              @submit=${(e: Event): void => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const input = form.elements.namedItem('item') as HTMLInputElement;
                if (addGroceryItem(homeId, { text: input.value })) input.value = '';
              }}
            >
              <input
                name="item"
                placeholder=${t('add_item')}
                aria-label=${t('add_item')}
                data-testid="grocery-input"
                maxlength="120"
              />
              <button type="submit" data-testid="grocery-add">+</button>
            </form>
          </div>`
        : null}
    </div>
  `;
}

function suggestionsCard(homeId: string): TemplateResult {
  return html`
    <div class=${`${styles.card} ${styles.suggestionsCard}`} data-testid="meals-suggestions">
      <div class=${styles.suggestionsTitle}>💡 ${t('quick_suggestions')}</div>
      <div
        class=${classMap({
          [styles.suggestionsGroupTitle!]: true,
          [styles.suggestionsGroupBreakfast!]: true,
        })}
      >
        ☕ ${t('breakfast')}
      </div>
      <div class=${styles.suggestionsList}>
        ${BREAKFAST_SUGGESTIONS.map(
          (item) =>
            html`<button
              type="button"
              class=${classMap({
                [styles.suggestion!]: true,
                [styles.suggestionBreakfast!]: true,
              })}
              data-testid=${`sugg-${item}`}
              @click=${(): void => {
                addGroceryItem(homeId, { text: item });
              }}
            >
              + ${item}
            </button>`,
        )}
      </div>
      <div
        class=${classMap({
          [styles.suggestionsGroupTitle!]: true,
          [styles.suggestionsGroupSnack!]: true,
        })}
      >
        🍎 ${t('snack')}
      </div>
      <div class=${styles.suggestionsList}>
        ${SNACK_SUGGESTIONS.map(
          (item) =>
            html`<button
              type="button"
              class=${classMap({
                [styles.suggestion!]: true,
                [styles.suggestionSnack!]: true,
              })}
              data-testid=${`sugg-${item}`}
              @click=${(): void => {
                addGroceryItem(homeId, { text: item });
              }}
            >
              + ${item}
            </button>`,
        )}
      </div>
    </div>
  `;
}

function applyAndClose(homeId: string, r: Recipe): void {
  if (!editingSlot) return;
  applyRecipeToSlot(homeId, editingSlot.dayIndex, editingSlot.service, r);
  editingSlot = null;
  recipeQuery = '';
  viewingRecipe = null;
  closeModal();
}

/** Ligne de résultat (base complète). Clic = applique ; 👁 = fiche. */
function fullRecipeRow(homeId: string, r: FullRecipe): TemplateResult {
  return html`<div class=${styles.recipeRowWrap}>
    <button
      type="button"
      class=${styles.recipeRow}
      data-testid=${`recipe-db-${r.id.toString()}`}
      @click=${(): void => {
        applyAndClose(homeId, toRecipe(r));
      }}
    >
      <span class=${styles.recipeIcon}>${iconForCategory(r.category)}</span>
      <div class=${styles.recipeMain}>
        <span class=${styles.recipeName}>${r.name_fr}</span>
        <span class=${styles.recipeMeta}
          >${r.category} · ${r.prep_time.toString()} min · ${r.servings.toString()} p.</span
        >
      </div>
    </button>
    <button
      type="button"
      class=${styles.recipeInfoBtn}
      aria-label=${t('recipe_view_sheet')}
      title=${t('recipe_view_sheet')}
      data-testid=${`recipe-sheet-${r.id.toString()}`}
      @click=${(): void => {
        viewingRecipe = r;
        bumpUi();
      }}
    >
      ${Icon(Icons.eye, 15)}
    </button>
  </div>`;
}

/** Fiche recette complète : méta, nutrition, ingrédients, étapes. */
function recipeSheetTemplate(homeId: string, r: FullRecipe): TemplateResult {
  const nut = r.nutrition;
  return html`<div class=${styles.recipeSheet} data-testid="recipe-sheet">
    <h3 class=${styles.recipeSheetTitle}>${iconForCategory(r.category)} ${r.name_fr}</h3>
    <p class=${styles.recipeMeta}>
      ${r.category} · ${r.cuisine} · ${r.prep_time.toString()} min · ${r.servings.toString()}
      ${t('recipe_servings')}
    </p>
    <div class=${styles.nutritionRow} data-testid="recipe-nutrition">
      <span>${nut.calories.toFixed(0)} ${t('nut_calories')}</span>
      <span>${nut.proteins.toFixed(0)} g ${t('nut_proteins')}</span>
      <span>${nut.carbs.toFixed(0)} g ${t('nut_carbs')}</span>
      <span>${nut.fat.toFixed(0)} g ${t('nut_fat')}</span>
      <span>${nut.fiber.toFixed(0)} g ${t('nut_fiber')}</span>
    </div>
    <h4 class=${styles.recipeSheetSub}>${t('ingredients_label')}</h4>
    <div class=${styles.recipeChips}>
      ${r.ingredients_flat.map((i) => html`<span class=${styles.recipeChip}>${i}</span>`)}
    </div>
    <h4 class=${styles.recipeSheetSub}>${t('recipe_steps')}</h4>
    <ol class=${styles.recipeSteps}>
      ${r.steps.map((s) => html`<li>${s}</li>`)}
    </ol>
    <div class=${styles.recipeSheetActions}>
      <button
        type="button"
        class=${styles.btnSecondary}
        data-testid="recipe-sheet-back"
        @click=${(): void => {
          viewingRecipe = null;
          bumpUi();
        }}
      >
        ${t('recipe_back')}
      </button>
      <button
        type="button"
        class=${styles.btnPrimary}
        data-testid="recipe-sheet-use"
        @click=${(): void => {
          applyAndClose(homeId, toRecipe(r));
        }}
      >
        ${t('recipe_use')}
      </button>
    </div>
  </div>`;
}

function recipePickerTemplate(homeId: string): TemplateResult {
  // Charge la base complète au premier rendu du picker (lazy, §2.4 :
  // en cas d'échec réseau on garde les 20 recettes du proto).
  const db = getRecipesDbNow();
  if (db === null) {
    void loadRecipesDb().then(() => {
      bumpUi();
    });
  }

  if (viewingRecipe) return recipeSheetTemplate(homeId, viewingRecipe);

  const query = recipeQuery.trim();
  const results = db && query.length >= 2 ? searchRecipes(db, query) : [];

  return html`
    <div data-testid="recipe-picker">
      <input
        type="search"
        class=${styles.recipeSearch}
        placeholder=${t('recipe_search_placeholder')}
        aria-label=${t('recipe_search_placeholder')}
        .value=${recipeQuery}
        data-testid="recipe-search"
        @input=${(e: Event): void => {
          recipeQuery = (e.target as HTMLInputElement).value;
          bumpUi();
        }}
      />
      ${query.length >= 2
        ? db === null
          ? html`<p class=${styles.recipeHint}>${t('recipe_db_loading')}</p>`
          : results.length === 0
            ? html`<p class=${styles.recipeHint}>${t('recipe_no_result')}</p>`
            : html`<div class=${styles.recipeList} data-testid="recipe-results">
                ${results.map((r) => fullRecipeRow(homeId, r))}
              </div>`
        : html`<p class=${styles.recipeHint}>${t('recipe_search_hint')}</p>
            <div class=${styles.recipeList} data-testid="recipe-picker-list">
              ${RECIPE_DB.map(
                (r) =>
                  html`<button
                    type="button"
                    class=${styles.recipeRow}
                    data-testid=${`recipe-${r.id.toString()}`}
                    @click=${(): void => {
                      applyAndClose(homeId, r);
                    }}
                  >
                    <span class=${styles.recipeIcon}>${r.icon}</span>
                    <div class=${styles.recipeMain}>
                      <span class=${styles.recipeName}>${r.name}</span>
                      <span class=${styles.recipeMeta}
                        >${r.cuisine} · ${r.time.toString()} min · ${r.diff}</span
                      >
                    </div>
                    ${r.tag ? html`<span class=${styles.recipeTag}>${r.tag}</span>` : null}
                  </button>`,
              )}
            </div>`}
    </div>
  `;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderMealsView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'classic') {
    return html`<section data-testid="view-meals" class=${styles.view}></section>`;
  }
  const data = home.data as ClassicHomeData;
  const modal = getModal();

  // Synchronise le label des jours avec la semaine en cours et la
  // langue (fidèle au proto qui calcule weekDates à chaque rendu).
  const today = new Date();
  const wd = weekDates(today, getLang());
  const meals: Meal[] = data.meals.map((m, i) => ({
    ...m,
    d: wd[i]?.label ?? m.d,
    today: wd[i]?.d === today.getDate(),
  }));

  // Cartouche stats gravé (audit D5) : une ligne typographique à la
  // place des 3 boîtes KPI — les métriques à zéro disparaissent.
  const plannedDays = meals.filter(
    (m) => !!(m.dinner?.trim() || m.lunch?.trim() || m.m.trim()),
  ).length;
  const groceryCount = data.grocery.length;
  const toPlan = 7 - plannedDays;
  const statParts: string[] = [
    ...(plannedDays > 0 ? [`${plannedDays.toString()}/7 ${t('home_kpi_meals_delta')}`] : []),
    ...(groceryCount > 0 ? [`${groceryCount.toString()} ${t('meals_kpi_courses_delta')}`] : []),
    ...(toPlan > 0 ? [`${toPlan.toString()} ${t('meals_kpi_to_plan_delta')}`] : []),
  ];

  return html`
    <section data-testid="view-meals" class=${styles.view}>
      ${editorialHero({
        badge: { mode: 'classic', label: home.name },
        dateLine: t('this_week'),
        title: t('meals_hero_title'),
        accent: t('meals_hero_accent'),
      })}
      ${statLine({ parts: statParts, testid: 'meals-kpi-strip' })}
      <div style="height: 16px;"></div>
      ${weekCard(home.id, meals)} ${groceryCard(home.id, data.grocery)} ${suggestionsCard(home.id)}
      <div class=${styles.actions}>
        <button
          type="button"
          class=${styles.btnPrimary}
          data-testid="btn-plan-meals"
          @click=${(): void => {
            // Par défaut on ouvre le picker pour le dîner du jour 0
            // (la modal applique sur `editingSlot` ; le user peut
            // aussi cliquer un slot précis directement).
            editingSlot = { dayIndex: 0, service: 'dinner' };
            openModal(MODAL_RECIPE);
          }}
        >
          ${t('plan_meals')}
        </button>
        <button
          type="button"
          class=${styles.btnSecondary}
          data-testid="btn-generate-week"
          @click=${(): void => {
            void generateWeeklyMenu(home.id);
          }}
        >
          ${t('generate_menu_week')}
        </button>
      </div>
      ${modal === MODAL_MEAL_SHEET && sheetSlot && data.meals[sheetSlot.dayIndex]
        ? modalTemplate({
            title:
              (sheetSlot.service === 'lunch'
                ? data.meals[sheetSlot.dayIndex]!.lunch
                : data.meals[sheetSlot.dayIndex]!.dinner || data.meals[sheetSlot.dayIndex]!.m) ??
              '',
            content: mealSheetTemplate(data.meals[sheetSlot.dayIndex]!, sheetSlot.service),
            onClose: (): void => {
              sheetSlot = null;
              closeModal();
            },
            closeLabel: t('close'),
            testid: 'modal-meal-sheet',
          })
        : null}
      ${modal === MODAL_RECIPE
        ? modalTemplate({
            title: t('pick_recipe'),
            content: recipePickerTemplate(home.id),
            onClose: (): void => {
              editingSlot = null;
              recipeQuery = '';
              viewingRecipe = null;
              closeModal();
            },
            closeLabel: t('close'),
            testid: 'modal-recipe',
          })
        : null}
    </section>
  `;
}
